import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import type { AnalyticsSettings } from '../../lib/analytics';
import { saveAnalyticsSettings } from '../../lib/analytics';

interface Props {
  settings: AnalyticsSettings;
  onUpdate: (updated: AnalyticsSettings) => void;
}

export default function AnalyticsSettingsPanel({ settings, onUpdate }: Props) {
  const [saving, setSaving] = useState(false);
  const [local, setLocal] = useState(settings);

  const handleToggle = async (field: keyof Pick<AnalyticsSettings, 'tracking_enabled' | 'realtime_enabled'>) => {
    const updated = { ...local, [field]: !local[field] };
    setLocal(updated);
    setSaving(true);
    try {
      await saveAnalyticsSettings(settings.id, { [field]: updated[field] });
      onUpdate(updated);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save');
      setLocal(local);
    } finally {
      setSaving(false);
    }
  };

  const handleRetention = async (days: number) => {
    const updated = { ...local, retention_days: days };
    setLocal(updated);
    setSaving(true);
    try {
      await saveAnalyticsSettings(settings.id, { retention_days: days });
      onUpdate(updated);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center space-x-2 mb-5">
        <Cog6ToothIcon className="w-5 h-5 text-gray-400" />
        <h3 className="text-sm font-semibold text-gray-700">Analytics Settings</h3>
      </div>

      <div className="space-y-4">
        <ToggleRow
          label="Enable tracking"
          description="Record page views and sessions for all visitors"
          checked={local.tracking_enabled}
          onChange={() => handleToggle('tracking_enabled')}
          disabled={saving}
        />

        <ToggleRow
          label="Realtime tracking"
          description="Show live visitor activity as it happens (uses Supabase Realtime)"
          checked={local.realtime_enabled}
          onChange={() => handleToggle('realtime_enabled')}
          disabled={saving || !local.tracking_enabled}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data retention
          </label>
          <select
            value={local.retention_days}
            onChange={e => handleRetention(Number(e.target.value))}
            disabled={saving}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
            <option value={365}>1 year</option>
          </select>
          <p className="text-xs text-gray-400 mt-1">Older data will not be automatically deleted — this is informational only.</p>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({
  label, description, checked, onChange, disabled
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between space-x-4">
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        <p className="text-xs text-gray-400">{description}</p>
      </div>
      <button
        type="button"
        onClick={onChange}
        disabled={disabled}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none disabled:opacity-50 ${checked ? 'bg-golf-orange' : 'bg-gray-200'}`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`}
        />
      </button>
    </div>
  );
}
